import { AlertTriangle, Mail, PenLine, Signature, UserRound } from 'lucide-react';
import React from 'react';
import { EMAIL_RE, FillVariable, SUB_LABELS, SubKind } from '../utils';
import { Label } from '@/components/ui-kit/label';
import { Input } from '@/components/ui-kit/input';
import { RenderTextForm } from './render-text-form';
import { ContactPicker } from '@/modules/org/contacts/components/ContactPicker';
import { useCompanyLookup } from '@/modules/org/companies/hooks/use-companies';

interface Props {
  parent: FillVariable;
  fieldErrors: Set<string>;
  childrenByParent: Map<string, FillVariable[]>;
  emails: Record<string, string>;
  setEmail: (personKey: string, value: string) => void;
  values: Record<string, string>;
  setValue: (name: string, value: string) => void;
  setCompanyValue: (name: string, value: string) => void;
  applyPersonValues: (
    parent: FillVariable,
    src: Partial<Record<SubKind, string | null | undefined>>
  ) => void;
}

export default function RenderPersonForm({
  parent,
  fieldErrors,
  childrenByParent,
  emails,
  setEmail,
  values,
  setCompanyValue,
  applyPersonValues,
  setValue,
}: Props) {
  // A picked contact stores a company-name snapshot taken when it was saved. The document
  // should carry the company's current name, so the linked record wins where it resolves.
  const { byId: companyById } = useCompanyLookup();
  const kids = childrenByParent.get(parent.id) ?? [];
  // When the person has a rendered email sub-field, it doubles as the signing email —
  // no dedicated input. Otherwise capture a dedicated (non-rendered) signing email.
  const hasEmailChild = kids.some((c) => c.subKind === 'email');
  const showDedicatedEmail = !!parent.isSignatory && !hasEmailChild;
  const emailVal = (emails[parent.name] ?? '').trim();
  // Live error for a typo'd address; required-but-empty surfaces once Generate is
  // attempted (flagged in fieldErrors under the person key).
  const emailBadFormat = emailVal !== '' && !EMAIL_RE.test(emailVal);
  const emailFlagged = fieldErrors.has(parent.name);
  const showEmailError = showDedicatedEmail && (emailBadFormat || emailFlagged);
  const emailErrorMsg =
    emailVal === ''
      ? "An email address is required — it's where the signing invitation is sent."
      : 'Enter a valid email address.';
  // Seed the "create new contact" form with whatever the user has already typed here.
  const bySub: Partial<Record<SubKind, string>> = {};
  for (const c of kids) if (c.subKind) bySub[c.subKind] = values[c.name] ?? '';
  const createDefaults = {
    name: bySub.name ?? '',
    email: bySub.email || (emails[parent.name] ?? ''),
    role: bySub.role ?? '',
    company: bySub.company ?? '',
    phone: bySub.phone ?? '',
    address: bySub.address ?? '',
  };
  return (
    <div className="space-y-4 rounded-xl border border-border bg-[hsl(var(--muted))]/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 text-selise-blue" />
          <span className="text-sm font-semibold text-[hsl(var(--fg))]">{parent.label}</span>
          {parent.isSignatory && (
            <span className="inline-flex items-center gap-1 rounded-full bg-selise-blue/10 px-2 py-0.5 text-[10px] font-semibold text-selise-blue">
              <PenLine className="h-2.5 w-2.5" />
              Signs
            </span>
          )}
        </div>

        <ContactPicker
          label="Use a contact"
          onSelect={(c) =>
            applyPersonValues(parent, {
              ...c,
              company: (c.companyId ? companyById.get(c.companyId)?.name : '') || c.company,
            })
          }
          createDefaults={createDefaults}
        />
      </div>
      <div className="space-y-4">
        {kids.map((child) => {
          // A signature sub-field is a location anchor, not a fill-in — show a read-only
          // marker instead of an input. It's excluded from progress/required and merge.
          if (child.subKind === 'signature') {
            return (
              <div
                key={child.name}
                className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-globe-grey"
              >
                <Signature className="h-3.5 w-3.5 shrink-0 text-selise-blue" />
                <span>{parent.label} signs here — captured in the document; no input needed.</span>
              </div>
            );
          }
          const isSigningEmail = parent.isSignatory && child.subKind === 'email';
          return (
            <div key={child.name}>
              <RenderTextForm
                invalid={fieldErrors.has(child.name)}
                value={values[child.name]}
                setCompanyValue={setCompanyValue}
                setValue={setValue}
                v={child}
                opts={{
                  subLabel: `${parent.label} — ${SUB_LABELS[child.subKind ?? 'name']}`,
                  forceRequired: isSigningEmail,
                  note: isSigningEmail
                    ? 'Shown in the document and used to send the signing invitation.'
                    : undefined,
                }}
              />
            </div>
          );
        })}
        {showDedicatedEmail && (
          <div className="space-y-2">
            <Label
              htmlFor={`email-${parent.name}`}
              className="flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--fg))]"
            >
              <Mail className="h-3.5 w-3.5 text-globe-grey" />
              {parent.label} — Signing email
              <span className="text-destructive" aria-hidden>
                *
              </span>
            </Label>
            <Input
              id={`email-${parent.name}`}
              type="email"
              required
              aria-invalid={showEmailError || undefined}
              aria-describedby={
                showEmailError ? `email-${parent.name}-error` : `email-${parent.name}-help`
              }
              value={emails[parent.name] ?? ''}
              onChange={(e) => setEmail(parent.name, e.target.value)}
              placeholder="name@company.com"
            />
            <p id={`email-${parent.name}-help`} className="text-xs text-globe-grey">
              Required to send for signature. Not added to the document.
            </p>
            {showEmailError && (
              <p
                id={`email-${parent.name}-error`}
                role="alert"
                className="flex items-center gap-1 text-xs text-destructive"
              >
                <AlertTriangle className="h-3 w-3" />
                {emailErrorMsg}
              </p>
            )}
          </div>
        )}
      </div>
      {/* TODO */}

      {/* {createDefaults.name.trim() !== "" && (
          <div className="flex justify-end border-t border-[hsl(var(--border))] pt-3">
            <SaveContactButton defaults={createDefaults} />
          </div>
        )} */}
    </div>
  );
}
